import { useContext, useEffect, useMemo, useState } from "react";

import { useParams, useNavigate, Link } from "react-router-dom";

import {
  Accordion,
  ActionIcon,
  Anchor,
  Button,
  Container,
  CopyButton,
  Divider,
  List,
  Skeleton,
  SkeletonProps,
  Table,
  TableThProps,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import {
  IconCheck,
  IconCopy,
  IconCreditCard,
  IconExternalLink,
  IconRestore,
} from "@tabler/icons-react";

import dayjs from "dayjs";

import { AppContext } from "../Wrapper";

import MyCartSection from "../../Components/Cart";

import { AppName, BrandImages, Colors } from "../../Lib/Data";
import { Endpoints } from "../../Lib/Endpoints";
import {
  DefaultSuccessNotification,
  generateRandomString,
  getDiscountedValue,
} from "../../Lib/Methods";
import { NonPaginatedResponse } from "../../Lib/Responses";
import {
  MySubscriptionProps,
  PlanProps,
  PossiblePeriods,
  PossibleServices,
  TransactionProps,
} from "../../Lib/Types";
import { usePerformRequest } from "../../Lib/usePerformRequest";

import "./styles.scss";

const SingleSubscriptionPage: React.FC = () => {
  const context = useContext(AppContext);
  const theme = context?.theme ?? "light";

  document.title = `Subscription Details | ${AppName}`;

  const { subscriptionID } = useParams();
  const {
    data,
    isLoading,
    reloadData: reloadSubscription,
  } = usePerformRequest<
    NonPaginatedResponse<{
      plan: PlanProps;
      subscription: MySubscriptionProps;
      transaction: TransactionProps;
    }>
  >({
    method: "GET",
    route: Endpoints.GetSingleSubscriptionWithPlan(subscriptionID as string),
  });

  const { transaction, plan, subscription } =
    data && data.data
      ? data?.data
      : { transaction: undefined, plan: undefined, subscription: undefined };

  const showLoginInformation = useMemo(
    () =>
      plan && plan.id
        ? ["Netflix", "Showmax", "Crunchyroll"].includes(plan.service)
        : false,
    [plan]
  );

  const [selectedPeriod, setSelectedPeriod] =
    useState<PossiblePeriods>("3 Months");
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [totalSaved, setTotalSaved] = useState<number>(0);

  useEffect(() => {
    if (
      context &&
      context.pricing &&
      context.pricing.pricing &&
      context.pricing.possiblePeriodsCost
    ) {
      let price = 0;
      if (
        subscription?.service === "iCloud - Apple Music" &&
        subscription.iCloudStorage
      ) {
        price =
          context.pricing.iCloudAppleMusicPricing.find(
            (i) => i.storage === subscription.iCloudStorage
          )?.price ?? 0;
      } else if (
        subscription?.service === "Google One" &&
        subscription.googleOneStorage
      ) {
        price =
          context.pricing.googleOneStoragePricing.find(
            (i) => i.storage === subscription.googleOneStorage
          )?.price ?? 0;
      } else {
        price =
          context?.pricing?.pricing.find((s) => {
            return s?.service === (subscription?.service as PossibleServices);
          })?.price ?? 0;
      }
      // UPDATE PRICE DEPENDING ON DURATION CHOSEN
      price =
        (price as number) *
        (context.pricing?.possiblePeriodsCost?.find(
          (p) => p.period === selectedPeriod
        )?.factor ?? 1);

      // CHECK FOR DISCOUNT DEPENDING ON DURATION
      const durationDiscount = context.pricing.durationDiscounts?.find(
        (d) => d.period === selectedPeriod
      )?.discount;
      let discounted = getDiscountedValue(price, durationDiscount ?? 0);
      setTotalAmount(discounted);
      setTotalSaved(price - discounted);
    }
  }, [selectedPeriod, context?.pricing, subscription]);

  const isSubscriptionExpired = () => {
    if (subscription) {
      return dayjs().isAfter(dayjs(subscription.expiresAt));
    } else {
      return true;
    }
  };
  const tableRowHeadProps: TableThProps = {
    style: {
      backgroundColor: theme === "dark" ? "transparent" : Colors.White,
      color: theme === "dark" ? Colors.White : Colors.BlackPrimary,
      borderRight: "1px solid #E0E0E0",
    },
  };
  const skeletonProps: SkeletonProps = {
    styles: {
      root: {
        background: theme === "dark" ? Colors.RedPrimary : Colors.White,
      },
    },
  };
  return (
    <Container size="md">
      <div className="single-subscription-container">
        <div className="flex-row align-center justify-between width-100">
          <div className="flex-col">
            <Text fw={500} fz={17} c={Colors.GreenSecondary}>
              REFERENCE
            </Text>
            {isLoading ? (
              <Skeleton {...skeletonProps} height={30} radius="xs" w="120px" />
            ) : (
              <CopyButton value={transaction?.reference ?? ""}>
                {({ copied, copy }) => (
                  <div className="flex-row align-center">
                    <Text c={copied ? Colors.GreenPrimary : Colors.BluePrimary}>
                      {data?.data.transaction.reference}
                    </Text>
                    <ActionIcon
                      ml={5}
                      size="sm"
                      variant="subtle"
                      onClick={copy}
                    >
                      <IconCopy size={16} />
                    </ActionIcon>
                  </div>
                )}
              </CopyButton>
            )}
          </div>

          <Link to={`/d/support/new?subscription=${subscriptionID}`}>
            <Button>Dispute</Button>
          </Link>
        </div>
        <img
          src={BrandImages.retrieveImage(
            subscription?.service as PossibleServices,
            theme
          )}
          className="service-img"
          alt=""
        />
        <Table
          mt={30}
          verticalSpacing="md"
          variant="vertical"
          layout="fixed"
          withRowBorders
          withTableBorder
        >
          <Table.Thead>
            <Table.Tr key={generateRandomString(12)}>
              <Table.Th {...tableRowHeadProps} w={100} fw={400}>
                Service
              </Table.Th>
              <Table.Td>
                {isLoading ? (
                  <Skeleton
                    {...skeletonProps}
                    height={25}
                    radius="xs"
                    w="100%"
                  />
                ) : (
                  <div className="flex-row align-center">
                    <Text
                      fz="sm"
                      c={theme === "dark" ? Colors.White : Colors.BlackPrimary}
                    >
                      {subscription?.service}
                    </Text>
                  </div>
                )}
              </Table.Td>
            </Table.Tr>
            {showLoginInformation && (
              <>
                <Table.Tr key={generateRandomString(12)}>
                  <Table.Th {...tableRowHeadProps} fw={400} w={140}>
                    Email
                  </Table.Th>
                  <Table.Td>
                    {isLoading ? (
                      <Skeleton
                        {...skeletonProps}
                        height={25}
                        radius="xs"
                        w="100%"
                      />
                    ) : (
                      <CopyButton value={plan?.login?.email as string}>
                        {({ copied, copy }) => (
                          <Tooltip
                            label={copied ? "Copied!" : "Copy to clipboard"}
                          >
                            <span
                              className="pointer"
                              onClick={copy}
                              style={{
                                color: copied
                                  ? Colors.GreenSecondary
                                  : Colors.BluePrimary,
                              }}
                            >
                              {plan?.login?.email}
                            </span>
                          </Tooltip>
                        )}
                      </CopyButton>
                    )}
                  </Table.Td>
                </Table.Tr>
                <Table.Tr key={generateRandomString(12)}>
                  <Table.Th {...tableRowHeadProps} fw={400}>
                    Password
                  </Table.Th>
                  <Table.Td>
                    {isLoading ? (
                      <Skeleton
                        {...skeletonProps}
                        height={25}
                        radius="xs"
                        w="100%"
                      />
                    ) : (
                      <CopyButton value={plan?.login?.password as string}>
                        {({ copied, copy }) => (
                          <Tooltip
                            label={copied ? "Copied!" : "Copy to clipboard"}
                          >
                            <span
                              className="pointer"
                              onClick={copy}
                              style={{
                                color: copied
                                  ? Colors.GreenSecondary
                                  : Colors.BluePrimary,
                              }}
                            >
                              {plan?.login?.password}
                            </span>
                          </Tooltip>
                        )}
                      </CopyButton>
                    )}
                  </Table.Td>
                </Table.Tr>
              </>
            )}
            {plan && plan.service === "Spotify Premium" && (
              <Table.Tr key={generateRandomString(12)}>
                <Table.Th {...tableRowHeadProps} fw={400}>
                  Invite Link
                </Table.Th>
                <Table.Td>
                  {isLoading ? (
                    <Skeleton
                      {...skeletonProps}
                      height={25}
                      radius="xs"
                      w="100%"
                    />
                  ) : (
                    <div className="flex-row align-center">
                      <CopyButton value={subscription?.spotifyLink as string}>
                        {({ copied, copy }) => (
                          <Button
                            radius="40px"
                            size="xs"
                            onClick={copy}
                            color={
                              copied ? Colors.GreenPrimary : Colors.BluePrimary
                            }
                            leftSection={<IconCopy size={15} />}
                          >
                            Copy
                          </Button>
                        )}
                      </CopyButton>
                      <Button
                        radius="40px"
                        size="xs"
                        onClick={() => {
                          window.open(subscription?.spotifyLink);
                        }}
                        ml={10}
                        color={Colors.GreenPrimary}
                        leftSection={<IconExternalLink size={15} />}
                      >
                        Open
                      </Button>
                    </div>
                  )}
                </Table.Td>
              </Table.Tr>
            )}
            {plan && plan.service === "Duolingo Super" && (
              <Table.Tr key={generateRandomString(12)}>
                <Table.Th {...tableRowHeadProps} fw={400}>
                  Invite Link
                </Table.Th>
                <Table.Td>
                  {isLoading ? (
                    <Skeleton
                      {...skeletonProps}
                      height={25}
                      radius="xs"
                      w="100%"
                    />
                  ) : (
                    <div className="flex-row align-center">
                      <CopyButton value={subscription?.duolingoLink as string}>
                        {({ copied, copy }) => (
                          <Button
                            radius="40px"
                            size="xs"
                            onClick={copy}
                            color={
                              copied ? Colors.GreenPrimary : Colors.BluePrimary
                            }
                            leftSection={<IconCopy size={15} />}
                          >
                            Copy
                          </Button>
                        )}
                      </CopyButton>
                      <Button
                        radius="40px"
                        size="xs"
                        onClick={() => {
                          window.open(subscription?.duolingoLink);
                        }}
                        ml={10}
                        color={Colors.GreenPrimary}
                        leftSection={<IconExternalLink size={15} />}
                      >
                        Open
                      </Button>
                    </div>
                  )}
                </Table.Td>
              </Table.Tr>
            )}
            {plan &&
              plan.service === "Spotify Premium" &&
              subscription.canRequestNewSpotifyLink && (
                <Table.Tr key={generateRandomString(12)}>
                  <Table.Th {...tableRowHeadProps} fw={400}>
                    Reset Invite
                  </Table.Th>
                  <Table.Td>
                    {isLoading ? (
                      <Skeleton
                        {...skeletonProps}
                        height={25}
                        radius="xs"
                        w="100%"
                      />
                    ) : (
                      <div className="flex-col">
                        <Text fz={14}>
                          If you need a new invite link to join the Spotify
                          Plan, you can use the button below to request.
                        </Text>

                        <Button
                          radius="40px"
                          variant="outline"
                          size="sm"
                          w={120}
                          onClick={() => {
                            window.open(subscription?.spotifyLink);
                          }}
                          mt={10}
                          color={Colors.GreenPrimary}
                          leftSection={<IconRestore size={15} />}
                        >
                          New
                        </Button>
                      </div>
                    )}
                  </Table.Td>
                </Table.Tr>
              )}
            <Table.Tr key={generateRandomString(12)}>
              <Table.Th {...tableRowHeadProps} fw={400}>
                Expires On
              </Table.Th>
              <Table.Td>
                {isLoading ? (
                  <Skeleton
                    {...skeletonProps}
                    height={25}
                    radius="xs"
                    w="100%"
                  />
                ) : (
                  <div className="flex-row align-center">
                    <Text
                      fz="sm"
                      c={
                        isSubscriptionExpired()
                          ? Colors.RedPrimary
                          : Colors.GreenPrimary
                      }
                    >
                      {dayjs(subscription?.expiresAt).format(
                        "ddd, DD MMM YYYY"
                      )}
                    </Text>
                  </div>
                )}
              </Table.Td>
            </Table.Tr>
          </Table.Thead>
        </Table>
        {isLoading ? (
          <Skeleton
            {...skeletonProps}
            height={80}
            mt={30}
            radius="xs"
            w="100%"
          />
        ) : (
          <>
            {subscription?.service === "Spotify Premium" &&
              plan?.service === "Spotify Premium" && (
                <div className="flex-col">
                  <Text fw={500} mt={30} fz={17} c={Colors.GreenSecondary}>
                    ADDRESS INFORMATION
                  </Text>
                  <Text>{plan.address.value}</Text>
                  <div className="flex-row width-100">
                    <img
                      src={plan.address.image}
                      alt=""
                      className="address-image"
                    />
                  </div>
                </div>
              )}
          </>
        )}
        <div className="flex-col">
          <Text fw={500} mt={30} mb={10} fz={19} c={Colors.GreenPrimary}>
            SETUP
          </Text>
          <Text c={theme === "dark" ? Colors.White : Colors.BlackPrimary}>
            {subscription?.service === "Netflix" && (
              <div className="flex-col setup">
                <List type="ordered">
                  <List.Item>
                    Install the Netflix App
                    <List listStyleType="disc">
                      <List.Item>
                        Open the App Store or Google Play Store on your mobile
                        device
                      </List.Item>
                      <List.Item>Search for "Netflix."</List.Item>
                      <List.Item>Download and Install the app</List.Item>
                    </List>
                  </List.Item>
                  <List.Item>
                    Sign In to Your Account
                    <List listStyleType="disc">
                      <List.Item>Once the app is installed, open it.</List.Item>
                      <List.Item>
                        Enter your email address and password associated with
                        your {AppName} Subscription.
                      </List.Item>
                    </List>
                  </List.Item>
                  <List.Item>Enjoy</List.Item>
                </List>
              </div>
            )}
            {subscription?.service === "Crunchyroll" && (
              <div className="flex-col setup">
                <List type="ordered">
                  <List.Item>
                    Install the Crunchyroll App
                    <List listStyleType="disc">
                      <List.Item>
                        Visit your device's app store (App Store, Google Play
                        Store, etc.)
                      </List.Item>
                      <List.Item>Search for "Crunchyroll"</List.Item>
                      <List.Item>
                        Download and install the official Crunchyroll app
                      </List.Item>
                    </List>
                  </List.Item>
                  <List.Item>
                    Sign In to Your Account
                    <List listStyleType="disc">
                      <List.Item>Open the Crunchyroll app</List.Item>
                      <List.Item>
                        Click on the profile icon or "Sign In" button
                      </List.Item>
                      <List.Item>
                        Enter the email and password provided in your {AppName}{" "}
                        subscription details
                      </List.Item>
                      <List.Item>
                        Click "Log In" to access your premium account
                      </List.Item>
                    </List>
                  </List.Item>
                  <List.Item>
                    Start Watching
                    <List listStyleType="disc">
                      <List.Item>
                        Browse through the available anime catalog
                      </List.Item>
                      <List.Item>Select your favorite show or movie</List.Item>
                      <List.Item>
                        Enjoy ad-free streaming with access to simulcasts and
                        the full library
                      </List.Item>
                    </List>
                  </List.Item>
                </List>
              </div>
            )}
            {subscription?.service === "YouTube Premium" && (
              <div className="flex-col setup">
                <List type="ordered">
                  <List.Item>
                    Accept the Invitation
                    <List listStyleType="disc">
                      <List.Item>
                        Check your email for an invitation from the YouTube
                        Premium family plan owner.
                      </List.Item>
                      <List.Item>
                        Open the invitation email and click on the **"Accept
                        Invitation"** button or link.
                      </List.Item>
                      <List.Item>
                        You will be redirected to YouTube to confirm your
                        membership.
                      </List.Item>
                    </List>
                  </List.Item>
                  <List.Item>
                    Sign In to Your Google Account
                    <List listStyleType="disc">
                      <List.Item>
                        If prompted, sign in with the **Google account** you
                        want to associate with the YouTube Premium Family Plan.
                      </List.Item>
                      <List.Item>
                        If you don't have a Google account, you will need to
                        create one.
                      </List.Item>
                      <List.Item>
                        Ensure you are using the same account the family manager
                        sent the invite to.
                      </List.Item>
                    </List>
                  </List.Item>
                  <List.Item>
                    Confirm Your Membership
                    <List listStyleType="disc">
                      <List.Item>
                        YouTube will display the details of your **YouTube
                        Premium Family Plan** benefits.
                      </List.Item>
                      <List.Item>
                        Click **"Join Family"** to confirm your acceptance.
                      </List.Item>
                      <List.Item>
                        Once confirmed, you will be redirected to YouTube.
                      </List.Item>
                    </List>
                  </List.Item>
                  <List.Item>
                    Start Enjoying YouTube Premium
                    <List listStyleType="disc">
                      <List.Item>
                        Open the YouTube app or visit YouTube:
                        <Anchor
                          ml={5}
                          href="https://www.youtube.com"
                          target="_blank"
                        >
                          https://www.youtube.com
                        </Anchor>
                      </List.Item>
                      <List.Item>
                        Sign in with your Google account if not already signed
                        in.
                      </List.Item>
                      <List.Item>
                        Enjoy **ad-free videos, background play, and offline
                        downloads** as part of your YouTube Premium benefits.
                      </List.Item>
                    </List>
                  </List.Item>
                </List>
              </div>
            )}
            {subscription?.service === "Spotify Premium" && (
              <div className="flex-col setup">
                <List type="ordered">
                  <List.Item>
                    Open the Spotify Invite Link
                    <List listStyleType="disc">
                      <List.Item>
                        Click on the Spotify invite link you received for the
                        family plan.
                      </List.Item>
                      <List.Item>
                        You will be redirected to a Spotify page to join the
                        family plan.
                      </List.Item>
                    </List>
                  </List.Item>
                  <List.Item>
                    Log In to Your Spotify Account
                    <List listStyleType="disc">
                      <List.Item>
                        If you already have a Spotify account, enter your email
                        and password.
                      </List.Item>
                      <List.Item>
                        If you don't have a Spotify account, click on "Sign Up"
                        to create a new account.
                      </List.Item>
                    </List>
                  </List.Item>
                  <List.Item>
                    Confirm Your Family Membership
                    <List listStyleType="disc">
                      <List.Item>
                        After logging in, you'll be asked to confirm your
                        membership in the family plan.
                      </List.Item>
                      <List.Item>
                        Use the address above to select your address
                      </List.Item>
                      <List.Item>
                        Follow the instructions to complete the process and join
                        the plan.
                      </List.Item>
                    </List>
                  </List.Item>
                  <List.Item>Enjoy Spotify Premium Family Plan</List.Item>
                </List>
              </div>
            )}
            {subscription?.service === "iCloud - Apple Music" && (
              <div className="flex-col setup">
                <List type="ordered" spacing="md" size="md" center>
                  <List.Item>
                    For iPhone or iPad:
                    <List listStyleType="disc">
                      <List.Item>
                        Open the Settings app on your device
                      </List.Item>
                      <List.Item>
                        Tap your name at the top of the screen
                      </List.Item>
                      <List.Item>Select "Family Sharing"</List.Item>
                      <List.Item>
                        Tap "Accept" when you receive the invitation
                      </List.Item>
                    </List>
                  </List.Item>
                  <List.Item>
                    For Mac:
                    <List listStyleType="disc">
                      <List.Item>
                        Click the Apple menu () in the top-left corner
                      </List.Item>
                      <List.Item>
                        Select "System Settings" (or "System Preferences")
                      </List.Item>
                      <List.Item>
                        Click your name at the top of the sidebar
                      </List.Item>
                      <List.Item>Select "Family Sharing"</List.Item>
                      <List.Item>
                        Click "Accept" when you receive the invitation
                      </List.Item>
                    </List>
                  </List.Item>
                  <List.Item>
                    After accepting the invitation:
                    <List listStyleType="disc">
                      <List.Item>Open the Apple Music app</List.Item>
                      <List.Item>
                        Sign in with your Apple ID if prompted
                      </List.Item>
                      <List.Item>
                        You'll now have access to the family plan's Apple Music
                        subscription
                      </List.Item>
                      <List.Item>
                        For iCloud storage, go to Settings {`>`} [Your Name]{" "}
                        {`>`} iCloud {`>`} Manage Storage to access the shared
                        storage
                      </List.Item>
                    </List>
                  </List.Item>
                  <List.Item>
                    Start enjoying your Apple Music and iCloud storage benefits!
                  </List.Item>
                </List>
              </div>
            )}
            {subscription?.service === "Duolingo Super" && (
              <div className="flex-col setup">
                <List spacing="md" size="sm" center type="ordered">
                  <List.Item>
                    Open the invite link you received from the Duolingo Family
                    Plan admin.
                  </List.Item>
                  <List.Item>
                    Log in to your Duolingo account, or create a new one if you
                    don't have one.
                  </List.Item>
                  <List.Item>
                    Once logged in, you'll see a prompt to join the Super Family
                    Plan.
                  </List.Item>
                  <List.Item>
                    Click <strong>"Join Plan"</strong> to confirm your
                    participation.
                  </List.Item>
                  <List.Item>
                    You'll now have access to all Super Duolingo features
                    instantly.
                  </List.Item>
                  <List.Item>
                    Open the Duolingo app and start learning without ads and
                    with enhanced features!
                  </List.Item>
                </List>
              </div>
            )}
            {subscription?.service === "Showmax" && (
              <div className="flex-col setup">
                <List type="ordered">
                  <List.Item>
                    Install the Showmax App
                    <List listStyleType="disc">
                      <List.Item>
                        Visit your device's app store (App Store, Google Play
                        Store)
                      </List.Item>
                      <List.Item>Search for "Showmax"</List.Item>
                      <List.Item>
                        Download and install the official Showmax app
                      </List.Item>
                    </List>
                  </List.Item>
                  <List.Item>
                    Sign In to Your Account
                    <List listStyleType="disc">
                      <List.Item>Open the Showmax app</List.Item>
                      <List.Item>
                        Click on "Sign In" or the profile icon
                      </List.Item>
                      <List.Item>
                        Enter your email address and password visible on your
                        dashboard
                      </List.Item>
                    </List>
                  </List.Item>
                  <List.Item>
                    Start streaming your favorite shows and movies!
                  </List.Item>
                </List>
              </div>
            )}
          </Text>

          {subscription?.expiresAt &&
            dayjs().isAfter(dayjs(subscription.expiresAt)) && (
              <>
                <Divider my={20} w="100%" />
                {isLoading ? (
                  <Skeleton
                    {...skeletonProps}
                    height={30}
                    radius="xs"
                    w="120px"
                  />
                ) : (
                  <Accordion mb={200} defaultValue="none">
                    <Accordion.Item key="extend" value="extend">
                      <Accordion.Control
                        icon={
                          <IconCreditCard
                            color={
                              theme === "dark"
                                ? Colors.GreenSecondary
                                : Colors.GreenPrimary
                            }
                          />
                        }
                      >
                        <Text fz="sm" fw={500} c={Colors.GreenSecondary}>
                          Renew Subscription
                        </Text>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Text
                          fz="sm"
                          fw={500}
                          c={
                            theme === "dark"
                              ? Colors.White
                              : Colors.BlackPrimary
                          }
                        >
                          Extend the expiry date of your subscription
                        </Text>
                        <MyCartSection
                          hideShadow={false}
                          iCloudStorage={null}
                          googleOneStorage={null}
                          isRenewalPayment={true}
                          subscriptionID={subscriptionID}
                          selectedServices={[
                            subscription?.service as PossibleServices,
                          ]}
                          selectedPeriod={selectedPeriod}
                          setSelectedPeriod={setSelectedPeriod}
                          totalAmount={totalAmount}
                          totalSaved={totalSaved}
                          handlePaymentSuccess={() => {
                            DefaultSuccessNotification("Payment completed!");
                            reloadSubscription();
                          }}
                        />
                      </Accordion.Panel>
                    </Accordion.Item>
                  </Accordion>
                )}
              </>
            )}
        </div>
      </div>
    </Container>
  );
};

export default SingleSubscriptionPage;
